export {}; // makes this file a module so `declare global` is valid

declare const firebase: any;
declare const mixpanel: { identify: (id: string) => void } | undefined;

declare global {
    interface Window {
        onGoogleSignIn: () => Promise<void>;
    }
}

const LOGIN_EMAIL_KEY = 'loginEmail';

function getQueryStringParameter(name: string): string {
    const escapedName = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + escapedName + '=([^&#]*)');
    const results = regex.exec(location.search);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

function prefillEmailFromStorage(): void {
    const existingEmail = window.localStorage && window.localStorage.getItem(LOGIN_EMAIL_KEY);
    if (existingEmail && !getQueryStringParameter('email')) {
        const input = document.getElementById('user_email') as HTMLInputElement | null;
        if (input) input.value = existingEmail;
    }
}

function wireResendVerification(): void {
    const link = document.getElementById('resend_verification_link');
    if (!link) return;

    link.addEventListener('click', async (e) => {
        e.preventDefault();

        const email =
            getQueryStringParameter('email') ||
            (document.getElementById('user_email') as HTMLInputElement | null)?.value;
        if (!email) return;

        link.textContent = 'Sending...';
        try {
            const csrfMeta = document.querySelector('meta[name="csrf-token"]');
            await fetch('/api/v1/users/email_verification/resend', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfMeta ? (csrfMeta as HTMLMetaElement).content : '',
                },
                body: JSON.stringify({ email }),
            });
            link.textContent = 'Verification email sent.';
        } catch (_err) {
            link.textContent = 'Could not resend. Please try again.';
        }
    });
}

function trackLogin(): void {
    const input = document.getElementById('user_email') as HTMLInputElement | null;
    const normalizedEmail = (input?.value ?? '').trim().toLowerCase();
    if (!normalizedEmail) return;

    if (typeof mixpanel !== 'undefined') {
        mixpanel.identify(normalizedEmail);
    }
    if (window.localStorage) {
        window.localStorage.setItem(LOGIN_EMAIL_KEY, normalizedEmail);
    }
}

function initFirebase(): void {
    if (typeof firebase === 'undefined') return;

    const form = document.getElementById('login_form');
    if (!form) return;

    const apiKey = form.dataset.firebaseApiKey;
    const projectId = form.dataset.firebaseProjectId;
    const emulatorHost = form.dataset.firebaseEmulatorHost;
    if (!apiKey || !projectId) return;

    firebase.initializeApp({
        apiKey,
        authDomain: `${projectId}.firebaseapp.com`,
        projectId,
    });

    if (emulatorHost) {
        firebase.auth().useEmulator(`http://${emulatorHost}`);
    }
}

async function onGoogleSignIn(): Promise<void> {
    const btn = document.getElementById('google_signin_btn') as HTMLButtonElement | null;
    const errorEl = document.getElementById('google_signin_error');
    const form = document.getElementById('login_form');
    if (!btn || !errorEl || !form) return;

    btn.disabled = true;
    errorEl.classList.add('hidden');
    errorEl.textContent = '';

    try {
        const provider = new firebase.auth.GoogleAuthProvider();
        const result = await firebase.auth().signInWithPopup(provider);
        const idToken = await result.user.getIdToken();

        const submitForm = document.createElement('form');
        submitForm.method = 'POST';
        submitForm.action = form.dataset.googleAuthPath ?? '';

        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'id_token';
        tokenInput.value = idToken;
        submitForm.appendChild(tokenInput);

        const csrfMeta = document.querySelector('meta[name="csrf-token"]');
        if (csrfMeta) {
            const csrfInput = document.createElement('input');
            csrfInput.type = 'hidden';
            csrfInput.name = 'authenticity_token';
            csrfInput.value = (csrfMeta as HTMLMetaElement).content;
            submitForm.appendChild(csrfInput);
        }

        document.body.appendChild(submitForm);
        submitForm.submit();
    } catch (error: any) {
        btn.disabled = false;
        console.error('[Google Sign-In] code:', error.code, 'message:', error.message);

        if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
            errorEl.textContent = 'Sign-in cancelled.';
        } else if (error.code === 'auth/popup-blocked') {
            errorEl.textContent = 'Pop-up was blocked by your browser. Please allow pop-ups for this page.';
        } else {
            errorEl.textContent = 'Google sign-in failed. Please try again or sign in with your email and password.';
        }
        errorEl.classList.remove('hidden');
    }
}

document.addEventListener('DOMContentLoaded', () => {
    prefillEmailFromStorage();
    wireResendVerification();
    initFirebase();
    document.getElementById('login_form')?.addEventListener('submit', trackLogin);
});

// GoogleSigninButton component uses onclick="onGoogleSignIn()" — keep it accessible globally
window.onGoogleSignIn = onGoogleSignIn;
